/**
 * Lightweight event bus for credit balance changes.
 *
 * Instead of polling every 10-15 seconds, components dispatch a
 * `credit-balance-changed` CustomEvent whenever a credit-consuming
 * action succeeds. Interested consumers (e.g. RealTimeCreditUpdater,
 * useCredits, dashboard widgets) subscribe once and only refresh
 * when credits actually change.
 *
 * Usage:
 *   import { notifyCreditChange } from 'utils/creditEvents';
 *   // after a credit-consuming action succeeds:
 *   notifyCreditChange();
 *
 *   import { onCreditChange } from 'utils/creditEvents';
 *   const unsub = onCreditChange(() => { ... });
 *   // later: unsub();
 */

import { store } from '../store';
import { deductCredits } from '../store/slices/creditsSlice';

const EVENT_NAME = 'credit-balance-changed';
let lastDeductionTimestamp = 0;

/** Dispatch a credit-balance-changed event and update Redux balance locally. Deduplicates rapid calls within 800ms. */
export function notifyCreditChange(cost?: number): void {
  const now = Date.now();
  if (now - lastDeductionTimestamp < 800) {
    return;
  }
  lastDeductionTimestamp = now;

  const deductAmount = cost !== undefined && Number(cost) >= 0 ? Number(cost) : 1;
  if (deductAmount > 0) {
    try {
      store.dispatch(deductCredits(deductAmount));
    } catch { }
  }
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { cost: deductAmount } }));
}

/**
 * Subscribe to credit balance changes.
 * Returns an unsubscribe function.
 */
export function onCreditChange(callback: () => void): () => void {
  const handler = () => callback();
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}
